import Pagination from '@mui/material/Pagination';
import React, { useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router-dom';

import { URLS } from '../../resources/constants';
import { useAllVendors } from '../../resources/useAllVendors';
import { useTax1099Api } from '../../resources/hooks/api/useTax1099ApiHook';
import { usePayerData } from '../../resources/hooks/usePayerDataHook';

import Tax1099Forms from '../Tax1099Forms';
import W9Modal from '../Tax1099Vendors/W9Modal';

type PortfolioFilter = 'all' | 'w9_missing' | 'ready' | 'w9_confirmed';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

/**
 * A candidate is what the server says it is. The row carries threshold_applied
 * for its box and tax year; the literals that stood here were the 2026 figures
 * and judged every earlier year against 2000 where the statute says 600. The
 * exempt and inactive tests mirror get_vendor_summary, so the cards, the tab
 * counts and the rows describe the same set.
 */
export const isCandidate = (v: any): boolean => {
  const gross = Number(v.ytd_gross || 0);
  const threshold = Number(v.threshold_applied ?? 2000);
  return v.status === 1 && v.is_1099_eligible !== 0 && gross >= threshold;
};

const Tax1099Portfolio: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('Tax1099Portfolio');
  const { branchId, businessName, maskedEin } = usePayerData();

  const [activeTab, setActiveTab] = useState<'portfolio' | 'forms'>('portfolio');

  // Card filter & drill-down state
  const [selectedFilter, setSelectedFilter] = useState<PortfolioFilter>('all');
  const [expandedEntity, setExpandedEntity] = useState<boolean>(true);
  const [w9ModalVendor, setW9ModalVendor] = useState<{ id: number; name: string } | null>(null);

  const {
    extractW9,
    confirmW9,
    fetchCurrentW9,
    downloadW9Pdf,
  } = useTax1099Api();

  // Shared across the 1099 screens: one fetch of the branch's vendors, reused
  // when the user moves between Portfolio, Payments and Year-End Review.
  const { vendors, first, loading, reload: loadData } = useAllVendors({ branchId });
  const totalCount = first.count ?? vendors.length;
  const summary = first.summary ?? null;

  // Derive real statistics strictly from actual vendor records & API summary
  const totalVendors = summary?.total ?? (totalCount || vendors.length);
  const qboCount = summary?.qbo_synced ?? vendors.filter((v) => v.qbo_vendor_id && v.source !== 'qbd').length;
  const qbdCount = summary?.qbd_synced ?? vendors.filter((v) => v.source === 'qbd').length;
  const syncedLabel = [
    qboCount > 0 && t('entity.qbo_synced', { count: qboCount }),
    qbdCount > 0 && t('entity.qbd_synced', { count: qbdCount }),
  ].filter(Boolean).join(' · ') || 'RevRex GL';
  const w9Confirmed = summary?.w9_on_file ?? vendors.filter((v) => Boolean(v.has_w9)).length;
  const w9Percent = totalVendors > 0 ? Math.round((w9Confirmed / totalVendors) * 100) : 0;

  const candidateVendors = useMemo(() => vendors.filter(isCandidate), [vendors]);

  // candidateVendors already applies the threshold; these only split by form type.
  const necCount = useMemo(
    () => candidateVendors.filter((v) => v.default_form_type !== '1099-MISC').length,
    [candidateVendors]
  );
  const miscCount = useMemo(
    () => candidateVendors.filter((v) => v.default_form_type === '1099-MISC').length,
    [candidateVendors]
  );

  // The summary is computed branch-wide in one query and is the authority. The
  // derived counts are the fallback, not the other way round -- preferring them
  // meant a truncated page silently overrode a correct server total.
  const w9Needed = summary?.w9_needed ?? candidateVendors.filter((v) => !v.has_w9).length;
  const readyCount = summary?.ready ?? candidateVendors.filter((v) => Boolean(v.has_w9)).length;
  const candidateCount = summary?.candidate_forms ?? candidateVendors.length;

  // Derive business initials for avatar
  const initials = useMemo(() => {
    const parts = businessName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return businessName.slice(0, 2).toUpperCase() || 'CP';
  }, [businessName]);

  const [entityContractorScope, setEntityContractorScope] = useState<'reportable' | 'all'>('reportable');

  const handleCardClick = (filter: PortfolioFilter) => {
    if (selectedFilter === filter) {
      if (filter !== 'all') {
        setSelectedFilter('all');
      } else {
        setExpandedEntity((prev) => !prev);
      }
    } else {
      setSelectedFilter(filter);
      setExpandedEntity(true);
    }
  };

  const filteredContractors = useMemo(() => {
    if (selectedFilter === 'w9_missing') {
      return candidateVendors.filter((v) => !v.has_w9);
    }
    if (selectedFilter === 'ready') {
      return candidateVendors.filter((v) => Boolean(v.has_w9));
    }
    if (selectedFilter === 'w9_confirmed') {
      return vendors.filter((v) => Boolean(v.has_w9));
    }
    // 'all' - when viewing client entity, show reportable candidates by default (or all paid vendors if toggled)
    if (entityContractorScope === 'reportable') {
      return candidateVendors;
    }
    const withPayments = vendors.filter((v) => Number(v.ytd_gross || 0) > 0);
    return withPayments.length > 0 ? withPayments : candidateVendors;
  }, [selectedFilter, candidateVendors, vendors, entityContractorScope]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Any change of filter, scope or page size can leave the current page past the
  // end of the list, which would render an empty table. Go back to the first page.
  useEffect(() => {
    setPage(1);
  }, [selectedFilter, entityContractorScope, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredContractors.length / pageSize));

  const pagedContractors = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredContractors.slice(start, start + pageSize);
  }, [filteredContractors, page, pageSize]);

  const entityMatchesFilter = useMemo(() => {
    if (!branchId) return false;
    if (selectedFilter === 'all') return true;
    if (selectedFilter === 'w9_missing') return w9Needed > 0;
    if (selectedFilter === 'ready') return readyCount > 0;
    if (selectedFilter === 'w9_confirmed') return w9Confirmed > 0;
    return true;
  }, [branchId, selectedFilter, w9Needed, readyCount, w9Confirmed]);

  return (
    <div className="py-2">
      {/* Top Header Mode Toggle */}
      <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
        <div className="d-flex gap-2">
          <Button
            size="sm"
            style={
              activeTab === 'portfolio'
                ? { backgroundColor: '#14509e', borderColor: '#14509e', color: '#fff', fontWeight: 700 }
                : {
                    backgroundColor: '#fff',
                    borderColor: '#14509e',
                    color: '#14509e',
                    fontWeight: 700,
                  }
            }
            variant={activeTab === 'portfolio' ? 'primary' : 'outline-primary'}
            onClick={() => setActiveTab('portfolio')}
          >
            {t('tabs.portfolio')}
          </Button>
          <Button
            size="sm"
            style={
              activeTab === 'forms'
                ? { backgroundColor: '#14509e', borderColor: '#14509e', color: '#fff', fontWeight: 700 }
                : {
                    backgroundColor: '#fff',
                    borderColor: '#14509e',
                    color: '#14509e',
                    fontWeight: 700,
                  }
            }
            variant={activeTab === 'forms' ? 'primary' : 'outline-primary'}
            onClick={() => setActiveTab('forms')}
          >
            {t('tabs.forms')}
          </Button>
        </div>
      </div>

      {activeTab === 'forms' ? (
        <Tax1099Forms />
      ) : (
        <>
          {/* VIEW 1: FIRM PORTFOLIO */}
          <div className="d-flex flex-wrap justify-content-between align-items-end mb-4 gap-3">
            <div>
              <p
                style={{
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontWeight: 800,
                  color: '#1687c8',
                  marginBottom: '4px',
                }}
              >
                {t('header.eyebrow')}
              </p>
              <h1 className="h3 font-weight-bold text-dark mb-1" style={{ color: '#0d2c55', fontWeight: 900 }}>
                {t('header.title')}
              </h1>
              <p className="text-muted small mb-0">
                {t('header.subtitle', { name: businessName })}
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="light"
                className="border fw-bold"
                style={{ backgroundColor: '#edf6fc', borderColor: '#c9e7f6', color: '#14509e' }}
                onClick={() => {
                  loadData();
                  toast.success(t('toast.refreshed', { name: businessName }));
                }}
              >
                {t('actions.refresh')}
              </Button>
              <Button
                variant="primary"
                className="fw-bold"
                style={{ backgroundColor: '#14509e', borderColor: '#14509e', borderRadius: '9px' }}
                onClick={() => navigate(URLS.VENDOR_CENTER)}
              >
                {t('actions.go_to_vendor_management')}
              </Button>
            </div>
          </div>

          {/* 4 REAL METRICS CARDS - CLICKABLE FILTERS */}
          <div className="row g-3 mb-4">
            {/* CARD 1: W-9s Confirmed */}
            <div className="col-12 col-sm-6 col-xl-3">
              <div
                role="button"
                tabIndex={0}
                className="card shadow-sm p-3 h-100"
                style={{
                  borderRadius: '14px',
                  border: selectedFilter === 'w9_confirmed' ? '2px solid #14509e' : '1px solid #dfe7f0',
                  backgroundColor: selectedFilter === 'w9_confirmed' ? '#f0f7ff' : '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease-in-out',
                  boxShadow:
                    selectedFilter === 'w9_confirmed'
                      ? '0 4px 12px rgba(20, 80, 158, 0.15)'
                      : '0 1px 3px rgba(0,0,0,0.05)',
                }}
                onClick={() => handleCardClick('w9_confirmed')}
                onKeyDown={(e) => e.key === 'Enter' && handleCardClick('w9_confirmed')}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <span
                    className="small fw-bold"
                    style={{
                      fontSize: '11px',
                      letterSpacing: '0.03em',
                      color: selectedFilter === 'w9_confirmed' ? '#14509e' : '#64748b',
                    }}
                  >
                    {t('filters.w9_confirmed')}
                  </span>
                  {selectedFilter === 'w9_confirmed' && (
                    <span
                      className="badge"
                      style={{
                        backgroundColor: '#dbeafe',
                        color: '#1e40af',
                        fontSize: '10px',
                        borderRadius: '999px',
                        padding: '2px 8px',
                      }}
                    >
                      {t('cards.active')}
                    </span>
                  )}
                </div>
                <div className="h3 fw-bold my-1" style={{ color: '#0d2c55' }}>
                  {totalVendors > 0 ? `${w9Percent}%` : '0%'}
                </div>
                <span className="small text-muted">
                  {t('cards.confirmed_of_total', { confirmed: w9Confirmed, total: totalVendors })}
                </span>
                <span
                  className="small mt-2 d-block"
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: selectedFilter === 'w9_confirmed' ? '#14509e' : '#94a3b8',
                  }}
                >
                  {selectedFilter === 'w9_confirmed' ? t('cards.filter_active_clear') : t('cards.click_to_filter')}
                </span>
              </div>
            </div>

            {/* CARD 2: the current entity, and the reset-to-all control */}
            <div className="col-12 col-sm-6 col-xl-3">
              <div
                role="button"
                tabIndex={0}
                className="card shadow-sm p-3 h-100"
                style={{
                  borderRadius: '14px',
                  border: selectedFilter === 'all' ? '2px solid #14509e' : '1px solid #dfe7f0',
                  backgroundColor: selectedFilter === 'all' ? '#f0f7ff' : '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease-in-out',
                  boxShadow:
                    selectedFilter === 'all'
                      ? '0 4px 12px rgba(20, 80, 158, 0.15)'
                      : '0 1px 3px rgba(0,0,0,0.05)',
                }}
                onClick={() => handleCardClick('all')}
                onKeyDown={(e) => e.key === 'Enter' && handleCardClick('all')}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <span
                    className="small fw-bold"
                    style={{
                      fontSize: '11px',
                      letterSpacing: '0.03em',
                      color: selectedFilter === 'all' ? '#14509e' : '#64748b',
                    }}
                  >
                    {t('cards.client_entity')}
                  </span>
                </div>
                <div
                  className="h5 fw-bold my-1 text-truncate"
                  style={{ color: '#0d2c55' }}
                  title={businessName || undefined}
                >
                  {branchId ? businessName || t('cards.unnamed_entity') : '--'}
                </div>
                <span className="small text-muted">
                  {branchId ? maskedEin || t('cards.no_ein') : t('cards.no_entity')}
                </span>
                <span
                  className="small mt-2 d-block"
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: selectedFilter === 'all' ? '#14509e' : '#94a3b8',
                  }}
                >
                  {selectedFilter === 'all' ? t('cards.showing_all') : t('cards.click_show_all')}
                </span>
              </div>
            </div>

            {/* CARD 3: W-9s Missing */}
            <div className="col-12 col-sm-6 col-xl-3">
              <div
                role="button"
                tabIndex={0}
                className="card shadow-sm p-3 h-100"
                style={{
                  borderRadius: '14px',
                  border: selectedFilter === 'w9_missing' ? '2px solid #b13c46' : '1px solid #dfe7f0',
                  backgroundColor: selectedFilter === 'w9_missing' ? '#fff8f8' : '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease-in-out',
                  boxShadow:
                    selectedFilter === 'w9_missing'
                      ? '0 4px 12px rgba(177, 60, 70, 0.18)'
                      : '0 1px 3px rgba(0,0,0,0.05)',
                }}
                onClick={() => handleCardClick('w9_missing')}
                onKeyDown={(e) => e.key === 'Enter' && handleCardClick('w9_missing')}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <span
                    className="small fw-bold"
                    style={{
                      fontSize: '11px',
                      letterSpacing: '0.03em',
                      color: selectedFilter === 'w9_missing' ? '#b13c46' : '#64748b',
                    }}
                  >
                    {t('filters.w9_missing')}
                  </span>
                  {selectedFilter === 'w9_missing' && (
                    <span
                      className="badge"
                      style={{
                        backgroundColor: '#fee2e2',
                        color: '#991b1b',
                        fontSize: '10px',
                        borderRadius: '999px',
                        padding: '2px 8px',
                      }}
                    >
                      {t('cards.filter_active_count', { count: w9Needed })}
                    </span>
                  )}
                </div>
                <div
                  className="h3 fw-bold my-1"
                  style={{ color: w9Needed > 0 ? '#b13c46' : '#0d2c55' }}
                >
                  {w9Needed}
                </div>
                <span className="small text-muted">
                  {w9Needed > 0 ? t('cards.action_required') : t('cards.no_missing_w9')}
                </span>
                <span
                  className="small mt-2 d-block"
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: selectedFilter === 'w9_missing' ? '#b13c46' : '#94a3b8',
                  }}
                >
                  {selectedFilter === 'w9_missing' ? t('cards.filter_active_clear') : t('cards.click_to_filter_count', { count: w9Needed })}
                </span>
              </div>
            </div>

            {/* CARD 4: Ready to Furnish */}
            <div className="col-12 col-sm-6 col-xl-3">
              <div
                role="button"
                tabIndex={0}
                className="card shadow-sm p-3 h-100"
                style={{
                  borderRadius: '14px',
                  border: selectedFilter === 'ready' ? '2px solid #137a55' : '1px solid #dfe7f0',
                  backgroundColor: selectedFilter === 'ready' ? '#f0fdf4' : '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease-in-out',
                  boxShadow:
                    selectedFilter === 'ready'
                      ? '0 4px 12px rgba(19, 122, 85, 0.18)'
                      : '0 1px 3px rgba(0,0,0,0.05)',
                }}
                onClick={() => handleCardClick('ready')}
                onKeyDown={(e) => e.key === 'Enter' && handleCardClick('ready')}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <span
                    className="small fw-bold"
                    style={{
                      fontSize: '11px',
                      letterSpacing: '0.03em',
                      color: selectedFilter === 'ready' ? '#137a55' : '#64748b',
                    }}
                  >
                    {t('filters.ready')}
                  </span>
                  {selectedFilter === 'ready' && (
                    <span
                      className="badge"
                      style={{
                        backgroundColor: '#dcfce7',
                        color: '#166534',
                        fontSize: '10px',
                        borderRadius: '999px',
                        padding: '2px 8px',
                      }}
                    >
                      {t('cards.filter_active_count', { count: readyCount })}
                    </span>
                  )}
                </div>
                <div className="h3 fw-bold my-1" style={{ color: '#137a55' }}>
                  {readyCount}
                </div>
                <span className="small text-muted">
                  {t('cards.total_candidates', { count: candidateCount })}
                </span>
                <span
                  className="small mt-2 d-block"
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: selectedFilter === 'ready' ? '#137a55' : '#94a3b8',
                  }}
                >
                  {selectedFilter === 'ready' ? t('cards.filter_active_clear') : t('cards.click_to_filter_count', { count: readyCount })}
                </span>
              </div>
            </div>
          </div>

          {/* CLIENT BUSINESS READINESS TABLE */}
          <div
            className="card shadow-sm border mb-4 bg-white"
            style={{ borderRadius: '16px', border: '1px solid #dfe7f0' }}
          >
            <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
              <div>
                <h6 className="fw-bold mb-1" style={{ color: '#0d2c55' }}>
                  {t('readiness.title')}
                </h6>
                <p className="text-muted small mb-0">
                  {t('readiness.subtitle', { name: businessName })}
                </p>
              </div>
            </div>

            {/* ACTIVE FILTER BANNER */}
            {selectedFilter !== 'all' && (
              <div
                className="px-3 py-2 border-bottom d-flex flex-wrap align-items-center justify-content-between gap-2"
                style={{
                  backgroundColor:
                    selectedFilter === 'w9_missing'
                      ? '#fff1f2'
                      : selectedFilter === 'ready'
                      ? '#f0fdf4'
                      : '#eff6ff',
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <span
                    className="badge"
                    style={{
                      backgroundColor:
                        selectedFilter === 'w9_missing'
                          ? '#fee2e2'
                          : selectedFilter === 'ready'
                          ? '#dcfce7'
                          : '#dbeafe',
                      color:
                        selectedFilter === 'w9_missing'
                          ? '#991b1b'
                          : selectedFilter === 'ready'
                          ? '#166534'
                          : '#1e40af',
                      fontSize: '12px',
                      padding: '4px 10px',
                      borderRadius: '999px',
                    }}
                  >
                    {t('banner.filter_prefix')}{' '}
                    {selectedFilter === 'w9_missing'
                      ? t('banner.w9_missing', { count: w9Needed })
                      : selectedFilter === 'ready'
                      ? t('banner.ready', { count: readyCount })
                      : t('banner.w9_confirmed', { count: w9Confirmed })}
                  </span>
                  <span className="text-muted small">
                    {selectedFilter === 'w9_missing' &&
                      t('banner.desc_w9_missing')}
                    {selectedFilter === 'ready' &&
                      t('banner.desc_ready')}
                    {selectedFilter === 'w9_confirmed' &&
                      t('banner.desc_w9_confirmed')}
                  </span>
                </div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px', fontWeight: 600 }}
                  onClick={() => setSelectedFilter('all')}
                >
                  {t('actions.clear_filter_show_all')}
                </Button>
              </div>
            )}

            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr className="text-muted small">
                    <th>{t('table.headers.client_business')}</th>
                    <th>{t('table.headers.w9_status')}</th>
                    <th>{t('table.headers.revrex_expenses')}</th>
                    <th>{t('table.headers.candidate_forms')}</th>
                    <th>{t('table.headers.current_status')}</th>
                    <th>{t('table.headers.owner_next_step')}</th>
                    <th className="text-end">{t('table.headers.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        <Spinner animation="border" size="sm" className="me-2" />
                        {t('table.loading')}
                      </td>
                    </tr>
                  )}

                  {!loading && !branchId && (
                    <tr>
                      <td colSpan={7} className="text-center py-5 text-muted">
                        <div className="mb-2" style={{ fontSize: '28px' }}>🏢</div>
                        <div className="fw-bold mb-1 text-dark">{t('empty.no_business_title')}</div>
                        <div className="small">
                          {t('empty.no_business_body')}
                        </div>
                      </td>
                    </tr>
                  )}

                  {!loading && branchId && !entityMatchesFilter && (
                    <tr>
                      <td colSpan={7} className="text-center py-5">
                        <div className="mb-2" style={{ fontSize: '32px' }}>
                          {selectedFilter === 'ready' ? '⏳' : '📄'}
                        </div>
                        <div className="fw-bold mb-1 text-dark" style={{ fontSize: '15px' }}>
                          {t('empty.no_match', {
                            filter:
                              selectedFilter === 'ready'
                                ? t('filters.ready')
                                : selectedFilter === 'w9_confirmed'
                                ? t('filters.w9_confirmed')
                                : t('filters.w9_missing'),
                          })}
                        </div>
                        <p className="text-muted small mb-3" style={{ maxWidth: '500px', margin: '0 auto' }}>
                          {selectedFilter === 'ready'
                            ? t('empty.ready_pending', { name: businessName, count: w9Needed })
                            : t('empty.no_certified', { name: businessName })}
                        </p>
                        <div className="d-flex justify-content-center gap-2">
                          {selectedFilter === 'ready' && w9Needed > 0 && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              style={{ fontWeight: 600, borderRadius: '8px' }}
                              onClick={() => setSelectedFilter('w9_missing')}
                            >
                              {t('actions.view_w9_missing', { count: w9Needed })}
                            </Button>
                          )}
                          <Button
                            variant="outline-primary"
                            size="sm"
                            style={{ fontWeight: 600, borderRadius: '8px' }}
                            onClick={() => setSelectedFilter('all')}
                          >
                            {t('actions.clear_filter')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )}

                  {!loading && branchId && entityMatchesFilter && (
                    <>
                      <tr>
                        <td>
                          <div className="d-flex align-items-center gap-3">
                            <div
                              style={{
                                width: '38px',
                                height: '38px',
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 900,
                                fontSize: '13px',
                                backgroundColor: '#edf6fc',
                                color: '#14509e',
                              }}
                            >
                              {initials}
                            </div>
                            <div>
                              <strong className="text-dark d-block">{businessName}</strong>
                              <small className="text-muted d-block">
                                EIN: <span className="font-monospace fw-bold">{maskedEin || t('cards.no_ein')}</span> · {t('entity.contractors', { count: totalVendors })} · {syncedLabel}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className="badge"
                            style={{
                              backgroundColor: totalVendors === 0 ? '#eff2f6' : w9Percent === 100 ? '#eaf7f1' : '#fffbeb',
                              color: totalVendors === 0 ? '#6b7280' : w9Percent === 100 ? '#137a55' : '#b45309',
                              fontWeight: 700,
                              borderRadius: '999px',
                              padding: '4px 8px',
                              fontSize: '10px',
                            }}
                          >
                            {totalVendors === 0 ? t('entity.no_vendors') : t('entity.confirmed_ratio', { confirmed: w9Confirmed, total: totalVendors })}
                          </span>
                        </td>
                        <td>
                          <span
                            className="badge"
                            style={{
                              backgroundColor: '#edf6fc',
                              color: '#14509e',
                              fontWeight: 700,
                              borderRadius: '999px',
                              padding: '4px 8px',
                              fontSize: '10px',
                            }}
                          >
                            {t('entity.live_sync')}
                          </span>
                        </td>
                        <td className="small text-dark font-weight-bold">
                          {necCount > 0 || miscCount > 0 ? t('entity.forms_breakdown', { nec: necCount, misc: miscCount }) : t('entity.zero_forms')}
                        </td>
                        <td>
                          <span
                            className="badge"
                            style={{
                              backgroundColor:
                                w9Needed > 0 ? '#fff0f1' : totalVendors === 0 ? '#eff2f6' : '#eaf7f1',
                              color:
                                w9Needed > 0 ? '#b13c46' : totalVendors === 0 ? '#6b7280' : '#137a55',
                              fontWeight: 700,
                              borderRadius: '999px',
                              padding: '4px 8px',
                              fontSize: '10px',
                            }}
                          >
                            {w9Needed > 0
                              ? t('entity.w9_needed', { count: w9Needed })
                              : totalVendors === 0
                              ? t('entity.no_contractors')
                              : t('entity.ready_signoff')}
                          </span>
                        </td>
                        <td>
                          <span className="small text-dark fw-bold">
                            {w9Needed > 0 ? t('entity.owner_vendor') : t('entity.owner_cpa')}{' '}
                          </span>
                          <span
                            className="small fw-semibold"
                            style={{ color: w9Needed > 0 ? '#b13c46' : '#137a55' }}
                          >
                            {w9Needed > 0
                              ? t('entity.collect_w9')
                              : readyCount > 0
                              ? t('entity.approve_year_end')
                              : t('entity.review_records')}
                          </span>
                        </td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end gap-1">
                            <Button
                              variant="light"
                              size="sm"
                              className="border py-1 px-2 fw-bold"
                              style={{
                                backgroundColor: expandedEntity ? '#e2e8f0' : '#fff',
                                borderColor: '#cbd5e1',
                                color: '#0f172a',
                                fontSize: '11px',
                                borderRadius: '6px',
                              }}
                              onClick={() => setExpandedEntity(!expandedEntity)}
                              title={expandedEntity ? t('actions.hide_breakdown') : t('actions.show_breakdown')}
                            >
                              {expandedEntity ? t('actions.hide') : t('actions.records', { count: filteredContractors.length })}
                            </Button>
                            <Button
                              variant="light"
                              size="sm"
                              className="border py-1 px-2 fw-bold"
                              style={{
                                backgroundColor: '#edf6fc',
                                borderColor: '#c9e7f6',
                                color: '#14509e',
                                fontSize: '11px',
                                borderRadius: '6px',
                              }}
                              onClick={() => navigate(URLS.VENDOR_CENTER)}
                            >
                              {t('actions.workspace')}
                            </Button>
                          </div>
                        </td>
                      </tr>

                      {/* CONTRACTOR RECORDS DRILL-DOWN */}
                      {expandedEntity && (
                        <tr style={{ backgroundColor: '#f8fafc' }}>
                          <td colSpan={7} className="p-3">
                            <div
                              className="card shadow-sm border p-3 bg-white"
                              style={{ borderRadius: '12px', borderColor: '#e2e8f0' }}
                            >
                              <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <span className="fw-bold small text-dark">
                                    {selectedFilter === 'w9_missing'
                                      ? t('contractors.title_w9_missing', { name: businessName })
                                      : selectedFilter === 'ready'
                                      ? t('contractors.title_ready', { name: businessName })
                                      : selectedFilter === 'w9_confirmed'
                                      ? t('contractors.title_w9_confirmed', { name: businessName })
                                      : entityContractorScope === 'reportable'
                                      ? t('contractors.title_reportable', { name: businessName })
                                      : t('contractors.title_all', { name: businessName })}
                                  </span>
                                  <span
                                    className="badge"
                                    style={{
                                      backgroundColor:
                                        selectedFilter === 'w9_missing'
                                          ? '#fee2e2'
                                          : selectedFilter === 'ready'
                                          ? '#dcfce7'
                                          : '#e0f2fe',
                                      color:
                                        selectedFilter === 'w9_missing'
                                          ? '#991b1b'
                                          : selectedFilter === 'ready'
                                          ? '#166534'
                                          : '#0369a1',
                                      fontSize: '11px',
                                      borderRadius: '999px',
                                      padding: '2px 8px',
                                    }}
                                  >
                                    {t('contractors.record_count', { count: filteredContractors.length })}
                                  </span>

                                  {selectedFilter === 'all' && (
                                    <div className="btn-group btn-group-sm ms-2" role="group">
                                      <button
                                        type="button"
                                        className={`btn btn-sm ${entityContractorScope === 'reportable' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        style={{
                                          fontSize: '11px',
                                          padding: '2px 8px',
                                          fontWeight: 600,
                                          ...(entityContractorScope === 'reportable'
                                            ? { backgroundColor: '#14509e', borderColor: '#14509e', color: '#fff' }
                                            : { backgroundColor: '#fff', color: '#64748b' }),
                                        }}
                                        onClick={() => setEntityContractorScope('reportable')}
                                      >
                                        {t('contractors.scope_reportable', { count: candidateVendors.length })}
                                      </button>
                                      <button
                                        type="button"
                                        className={`btn btn-sm ${entityContractorScope === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        style={{
                                          fontSize: '11px',
                                          padding: '2px 8px',
                                          fontWeight: 600,
                                          ...(entityContractorScope === 'all'
                                            ? { backgroundColor: '#14509e', borderColor: '#14509e', color: '#fff' }
                                            : { backgroundColor: '#fff', color: '#64748b' }),
                                        }}
                                        onClick={() => setEntityContractorScope('all')}
                                      >
                                        {t('contractors.scope_all', { count: vendors.filter((v) => Number(v.ytd_gross || 0) > 0).length })}
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="text-decoration-none p-0 small fw-bold"
                                  style={{ color: '#14509e' }}
                                  onClick={() => navigate(URLS.VENDOR_CENTER)}
                                >
                                  {t('actions.open_vendor_management')}
                                </Button>
                              </div>

                              {filteredContractors.length === 0 ? (
                                <div className="text-center py-3 text-muted small">
                                  {t('empty.no_records_match', { name: businessName })}
                                </div>
                              ) : (
                                <div className="table-responsive">
                                  <table className="table table-sm table-hover align-middle mb-0" style={{ fontSize: '12px' }}>
                                    <thead>
                                      <tr className="text-muted" style={{ fontSize: '11px' }}>
                                        <th>{t('contractors.headers.vendor')}</th>
                                        <th>{t('contractors.headers.vendor_code')}</th>
                                        <th>{t('contractors.headers.ytd_gross')}</th>
                                        <th>{t('contractors.headers.form_candidate')}</th>
                                        <th>{t('contractors.headers.w9_status')}</th>
                                        <th>{t('contractors.headers.next_action')}</th>
                                        <th className="text-end">{t('contractors.headers.actions')}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {pagedContractors.map((c: any) => {
                                        const gross = Number(c.ytd_gross || 0);
                                        const threshold = Number(c.threshold_applied ?? 2000);
                                        const isReportable = isCandidate(c);
                                        const hasW9 = Boolean(c.has_w9);
                                        return (
                                          <tr key={c.id}>
                                            <td>
                                              <strong className="text-dark d-block">{c.display_name}</strong>
                                              {c.legal_name && c.legal_name !== c.display_name && (
                                                <span className="text-muted small" style={{ fontSize: '11px' }}>
                                                  {t('contractors.legal', { name: c.legal_name })}
                                                </span>
                                              )}
                                            </td>
                                            <td>
                                              <span className="font-monospace text-muted">{c.vendor_code || `ID-${c.id}`}</span>
                                            </td>
                                            <td>
                                              <span className="fw-bold text-dark">
                                                ${gross.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                              </span>
                                              {!isReportable && gross > 0 && (
                                                <span className="d-block text-muted" style={{ fontSize: '10px' }}>
                                                  {t('contractors.to_limit', {
                                                    remaining: (threshold - gross).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                                                    threshold,
                                                  })}
                                                </span>
                                              )}
                                            </td>
                                            <td>
                                              <span className="badge bg-light text-dark border">
                                                {c.default_form_type || '1099-NEC'}
                                              </span>
                                            </td>
                                            <td>
                                              {hasW9 ? (
                                                <span
                                                  className="badge"
                                                  style={{ backgroundColor: '#eaf7f1', color: '#137a55', borderRadius: '999px', padding: '3px 8px' }}
                                                >
                                                  {t('contractors.status.confirmed')}
                                                </span>
                                              ) : isReportable ? (
                                                <span
                                                  className="badge"
                                                  style={{ backgroundColor: '#fee2e2', color: '#b13c46', borderRadius: '999px', padding: '3px 8px' }}
                                                >
                                                  {t('contractors.status.needed')}
                                                </span>
                                              ) : (
                                                <span
                                                  className="badge"
                                                  style={{ backgroundColor: '#eff2f6', color: '#64748b', borderRadius: '999px', padding: '3px 8px' }}
                                                  title={t('contractors.status.under_threshold_tooltip', { threshold })}
                                                >
                                                  {t('contractors.status.under_threshold')}
                                                </span>
                                              )}
                                            </td>
                                            <td>
                                              <span
                                                className="small fw-semibold"
                                                style={{
                                                  color: hasW9 ? '#137a55' : isReportable ? '#b13c46' : '#64748b',
                                                }}
                                              >
                                                {hasW9
                                                  ? t('contractors.next.ready')
                                                  : isReportable
                                                  ? t('contractors.next.collect')
                                                  : t('contractors.next.none', { threshold: threshold.toLocaleString() })}
                                              </span>
                                            </td>
                                            <td className="text-end">
                                              <div className="d-flex justify-content-end gap-1">
                                                <Button
                                                  variant={hasW9 ? 'outline-secondary' : isReportable ? 'outline-danger' : 'outline-secondary'}
                                                  size="sm"
                                                  style={{
                                                    fontSize: '11px',
                                                    padding: '2px 8px',
                                                    fontWeight: 600,
                                                    borderRadius: '6px',
                                                    ...(isReportable || hasW9 ? {} : { color: '#64748b', borderColor: '#cbd5e1' }),
                                                  }}
                                                  onClick={() => setW9ModalVendor({ id: c.id, name: c.display_name })}
                                                >
                                                  {hasW9 ? t('actions.view_w9') : isReportable ? t('actions.collect_w9') : t('actions.add_w9')}
                                                </Button>
                                                <Button
                                                  variant="light"
                                                  size="sm"
                                                  className="border"
                                                  style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px' }}
                                                  onClick={() => navigate(URLS.TAX_1099_PAYMENTS)}
                                                >
                                                  {t('actions.ledger')}
                                                </Button>
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                  <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                      <span className="text-muted small">{t('pagination.rows_per_page')}</span>
                                      <Form.Select
                                        size="sm"
                                        style={{ width: 90 }}
                                        value={pageSize}
                                        onChange={(event) => setPageSize(Number(event.target.value))}
                                      >
                                        {PAGE_SIZE_OPTIONS.map((option) => (
                                          <option key={option} value={option}>{option}</option>
                                        ))}
                                      </Form.Select>
                                      <span className="text-muted small">
                                        {t('pagination.showing', {
                                          from: (page - 1) * pageSize + 1,
                                          to: Math.min(page * pageSize, filteredContractors.length),
                                          total: filteredContractors.length,
                                        })}
                                      </span>
                                    </div>
                                    <Pagination
                                      count={totalPages}
                                      page={page}
                                      onChange={(_event, value) => setPage(value)}
                                      size="small"
                                      color="primary"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* REAL DATA PORTFOLIO NOTICE */}
          <div
            className="card p-3 border"
            style={{ backgroundColor: '#f8fafc', borderColor: '#e2e8f0', borderRadius: '12px' }}
          >
            <div className="d-flex align-items-start gap-3">
              <div style={{ fontSize: '20px' }}>ℹ️</div>
              <div>
                <h6 className="fw-bold mb-1" style={{ color: '#0d2c55' }}>
                  {t('notice.title')}
                </h6>
                <p className="small text-muted mb-1">
                  <Trans
                    t={t}
                    i18nKey="notice.current_entity"
                    values={{ name: businessName, ein: maskedEin || t('notice.not_configured') }}
                    components={{ bold: <strong />, mono: <span className="font-monospace fw-bold" /> }}
                  />
                </p>
                <p className="small text-muted mb-0">
                  {t('notice.multi_client')}
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {w9ModalVendor && branchId && (
        <W9Modal
          show={Boolean(w9ModalVendor)}
          vendorId={w9ModalVendor.id}
          vendorName={w9ModalVendor.name}
          branchId={branchId}
          onClose={() => setW9ModalVendor(null)}
          extractW9={extractW9}
          confirmW9={confirmW9}
          fetchCurrentW9={fetchCurrentW9}
          downloadW9Pdf={downloadW9Pdf}
          onConfirmed={() => {
            setW9ModalVendor(null);
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default Tax1099Portfolio;
